import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import {
	Button,
	Divider,
	EmptyRow,
	Flex,
	Pagination,
	SearchInput,
	Spinner,
	Status,
	Table,
	Td,
	Tr,
} from '@components'
import Breadcrumb from '@components/Breadcrumb'
import Template from '@components/Template'
import UserBanner from '@components/UserBanner'
import { successMessages } from '@constants/feedbackMessages'
import {
	ArchiveIcon,
	PencilAltIcon,
	RefreshIcon,
	TrashIcon,
} from '@heroicons/react/outline'
import { ExamsArchivedFilter } from '@models/exams.model'
import Loading from '@pages/Loading'
import { categoryApi } from '@services/CategoryApi'
import { examsApi } from '@services/ExamsApi'
import {
	loadingModal,
	openAlertModal,
	openErrorModal,
	openModal,
} from '@store/ducks/modal'
import { notificationCreators } from '@store/ducks/notification'
import { useAppDispatch } from '@store/hooks'
import {
	createBreadcrumbLink,
	useCreateBreadcrumb,
	useFilters,
	useInputBounce,
	useQuery,
} from '@utils/hooks'

import EditCategory from '../EditCategory'
import AddExams from './AddExams'
import EditExams from './EditExams'

const styles = {
	searchContainer: 'gap-[8px] my-[32px] items-end flex-wrap',
	tag: 'font-medium text-blue-600 underline underline-offset-2',
	divider: 'mt-[32px] mb-[12px]',
	avatar: 'rounded-full max-w-[40px] max-h-[40px] min-w-[40px] min-h-[40px]',
	filters: 'gap-[8px] mb-[16px]',
}

function getApiErrorMessage(error: unknown) {
	const data = (error as { data?: { message?: string } })?.data

	return (
		data?.message ||
		'Não foi possível remover o simulado. Caso o erro persista, entre em contato com a engenharia.'
	)
}

function IndividualCategory() {
	const dispatch = useAppDispatch()
	const query = useQuery()
	const navigate = useNavigate()

	const pageQuery = Number(query.get('page')) || 1
	const searchQs = query.get('search') || ''
	const [search, setSearch] = useState(decodeURIComponent(searchQs))
	const [searchQuery, setSearchQuery] = useState(decodeURIComponent(searchQs))
	const [page, setPage] = useState(pageQuery)
	const [archivedFilter, setArchivedFilter] =
		useState<ExamsArchivedFilter>('active')

	const showingArchived = archivedFilter === 'archived'

	const { id } = useParams<{
		id: string
	}>()

	const { data: categoryInfo } = categoryApi.useShowQueryQuery({
		id: String(id) as string,
	})

	const { data, isLoading } = examsApi.useListQuery({
		id,
		page,
		search: encodeURIComponent(searchQuery),
		archived: archivedFilter,
	})

	const allPages = useMemo(() => {
		return [
			createBreadcrumbLink(`Categorias`, `/categorias`, 1),
			createBreadcrumbLink(`${categoryInfo?.name}`, `/categorias/${id}`, 2),
		]
	}, [categoryInfo?.name, id])

	const pages = useCreateBreadcrumb(allPages)

	const [
		deleteDiscipline,
		{ isLoading: loadingDelete, isSuccess: deleteSuccess, error: deleteError },
	] = examsApi.useDeleteMutation()

	const [
		archiveExam,
		{ isLoading: loadingArchive, isSuccess: archiveSuccess },
	] = examsApi.useArchiveMutation()

	const [
		unarchiveExam,
		{ isLoading: loadingUnarchive, isSuccess: unarchiveSuccess },
	] = examsApi.useUnarchiveMutation()

	const searchCallback = useCallback(
		(value: string) => {
			navigate(`/categorias/${id}`)
			setSearchQuery(value)
		},
		[id, navigate]
	)

	const changeArchivedFilter = useCallback(
		(filter: ExamsArchivedFilter) => {
			navigate(`/categorias/${id}`)
			setPage(1)
			setArchivedFilter(filter)
		},
		[id, navigate]
	)

	useEffect(() => {
		dispatch(
			loadingModal({
				loading: loadingDelete || loadingArchive || loadingUnarchive,
			})
		)

		if (deleteSuccess) {
			dispatch(notificationCreators.show(successMessages.deleteExams))
		}

		if (archiveSuccess) {
			dispatch(notificationCreators.show(successMessages.archivedExams))
		}

		if (unarchiveSuccess) {
			dispatch(notificationCreators.show(successMessages.unarchivedExams))
		}
	}, [
		dispatch,
		loadingDelete,
		loadingArchive,
		loadingUnarchive,
		deleteSuccess,
		archiveSuccess,
		unarchiveSuccess,
	])

	useEffect(() => {
		if (!deleteError) {
			return
		}

		dispatch(
			openErrorModal({
				title: 'Não foi possível remover',
				message: getApiErrorMessage(deleteError),
				buttonLabel: 'Entendi',
			})
		)
	}, [dispatch, deleteError])

	useEffect(() => {
		setPage(pageQuery)
	}, [pageQuery])

	useInputBounce(search, searchCallback)

	useFilters({
		search: searchQuery,
		fallbackUrl: '/categorias',
	})

	const options = [
		{
			label: 'Editar Categoria',
			onClick: () =>
				dispatch(
					openModal({
						name: 'editCategory',
						modalInfo: id,
					})
				),
		},
	]

	if (isLoading) {
		return <Loading />
	}

	return (
		<>
			<Template>
				<Breadcrumb pages={pages} />
				<UserBanner
					dontShowAvatar
					name={`${categoryInfo?.name}`}
					options={options}
				/>

				<Flex className={styles.searchContainer}>
					<SearchInput
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						className='flex-1'
						placeholder='Buscar Simulado'
					/>

					<Button
						onClick={() =>
							dispatch(
								openModal({
									name: 'addExams',
									modalInfo: id,
								})
							)
						}
					>
						Adicionar Simulado
					</Button>
				</Flex>

				<Flex className={styles.filters}>
					<Button
						variant='outlineActive'
						active={!showingArchived}
						onClick={() => changeArchivedFilter('active')}
					>
						Ativos
					</Button>
					<Button
						variant='outlineActive'
						active={showingArchived}
						onClick={() => changeArchivedFilter('archived')}
					>
						Arquivados
					</Button>
				</Flex>

				{isLoading ? (
					<div className='flex justify-center'>
						<Spinner size='md' />
					</div>
				) : (
					<Table
						th={[
							{ label: 'id' },
							{ label: 'nome' },
							{ label: 'status' },
							{ label: 'ações' },
						]}
					>
						{data?.data && data?.data?.length > 0 ? (
							data?.data.map(({ id: simulatedId, name, archivedAt }) => (
								<Tr key={simulatedId}>
									<Td>{simulatedId}</Td>
									<Td className='w-[65%] px-6'>
										<Link to={`/categorias/${id}/simulado/${simulatedId}`}>
											<Flex className='items-center gap-4'>{name}</Flex>
										</Link>
									</Td>
									<Td>
										<Status status={archivedAt ? 'warn' : 'success'}>
											{archivedAt ? 'Arquivado' : 'Ativo'}
										</Status>
									</Td>
									<Td>
										<Flex className='items-center gap-4'>
											<Button
												className='!px-2 !py-2'
												leftIcon={<PencilAltIcon />}
												disabled={false}
												variant='outline'
												onClick={() =>
													dispatch(
														openModal({
															name: 'editExams',
															modalInfo: simulatedId,
														})
													)
												}
											>
												<></>
											</Button>

											{archivedAt ? (
												<Button
													className='!px-2 !py-2'
													leftIcon={<RefreshIcon />}
													variant='outline'
													onClick={() => {
														dispatch(
															openAlertModal({
																type: 'alert-green',
																title: 'Reativar Simulado',
																message:
																	'O simulado voltará a aparecer para os alunos. Deseja continuar?',
																buttonLabel: 'Reativar Simulado',
																onClick: () =>
																	unarchiveExam({ id: simulatedId }),
															})
														)
													}}
												>
													<></>
												</Button>
											) : (
												<Button
													className='!px-2 !py-2'
													leftIcon={<ArchiveIcon />}
													variant='outline'
													onClick={() => {
														dispatch(
															openAlertModal({
																title: 'Arquivar Simulado',
																message:
																	'O simulado sai do app dos alunos e o histórico de respostas deles é preservado. Você pode reativá-lo depois.',
																buttonLabel: 'Arquivar Simulado',
																onClick: () => archiveExam({ id: simulatedId }),
															})
														)
													}}
												>
													<></>
												</Button>
											)}

											<Button
												className='!px-2 !py-2'
												leftIcon={<TrashIcon />}
												disabled={false}
												variant='outline'
												onClick={() => {
													const removeExam = {
														title: 'Remover Simulado',
														message:
															'Remover apaga o simulado e todo o histórico dos alunos nele (respostas, notas e ranking). Se a ideia é apenas tirar do ar, arquive o simulado.',
														buttonLabel: 'Remover Simulado',
														onClick: () =>
															deleteDiscipline({
																id: simulatedId,
															}),
													}

													dispatch(openAlertModal(removeExam))
												}}
											>
												<></>
											</Button>
										</Flex>
									</Td>
								</Tr>
							))
						) : (
							<EmptyRow
								tdAmount={4}
								message={
									showingArchived
										? 'Nenhum simulado arquivado'
										: 'Nenhum simulado encontrado'
								}
							/>
						)}
					</Table>
				)}
				<Divider className={styles.divider} />
				{data?.data && data?.data?.length > 0 && (
					<Pagination
						pageCount={data?.meta.lastPage || 0}
						maxAmount={data?.meta.total || 0}
						perPage={data?.meta.perPage || 0}
					/>
				)}
			</Template>
			<EditCategory />
			<AddExams />
			<EditExams />
		</>
	)
}

export default IndividualCategory
